import { create } from 'zustand';

/**
 * useNarrationStore — Phase 7 Plan 07-02 (AUDIO-03 v1 stand-in).
 *
 * Zustand slice tracking the NarrationPlayer's playback state. Kept separate
 * from useTourStore because:
 *   - useTourStore owns tour/caption semantics (which tour, which step, which
 *     caption text); it's driven by TourRig's useFrame loop.
 *   - useNarrationStore owns audio playback plumbing (which mp3, muted, current
 *     time); it's driven by the <audio> element + user mute toggle.
 *
 * NarrationPlayer mirrors useTourStore.activeTourId into this store via useEffect
 * so the two stores stay in sync without coupling the tour state machine to the
 * audio element directly. Plan 07-06 wires MuteButton to setMuted.
 *
 * Shape:
 *   - activeTourId: string | null — which /audio/tours/{id}.mp3 to play
 *   - playing: boolean — derived flag; true when activeTourId !== null
 *   - muted: boolean — user has explicitly muted narration
 *   - currentTime: number — read from <audio> timeupdate; currently unused at UI
 *     level but exposed for future scrubber / progress-indicator work
 *
 * Actions:
 *   - setActiveTour(id | null): sets activeTourId; playing becomes (id !== null)
 *   - setMuted(muted): toggles muted; does NOT alter activeTourId or playing
 *   - setCurrentTime(t): updates currentTime from the <audio> timeupdate event
 */
export interface NarrationState {
  activeTourId: string | null;
  playing: boolean;
  muted: boolean;
  currentTime: number;
  setActiveTour: (id: string | null) => void;
  setMuted: (muted: boolean) => void;
  setCurrentTime: (t: number) => void;
}

export const useNarrationStore = create<NarrationState>((set) => ({
  activeTourId: null,
  playing: false,
  muted: false,
  currentTime: 0,
  setActiveTour: (id) => set({ activeTourId: id, playing: id !== null }),
  setMuted: (muted) => set({ muted }),
  setCurrentTime: (t) => set({ currentTime: t }),
}));
